import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { validateAndCategorizeColumns } from '@/components/ColumnsPicker/utils'
import { TableColumnFlat } from '@/store/modules/table/types'

interface UseDeprecatedColumnsProps {
	onRemoveDeprecatedColumns?: (cleanedColumns: TableColumnFlat[]) => void
	pickedColumns?: TableColumnFlat[]
	tableColumns: TableColumnFlat[] | undefined
}

/**
 * A hook to handle deprecated columns warning and modal functionality
 * Used by both ForeignKey and ColumnsPicker components
 */
export const useDeprecatedColumns = ({
	tableColumns,
	pickedColumns: externalPickedColumns,
	onRemoveDeprecatedColumns,
}: UseDeprecatedColumnsProps) => {
	const { control, setValue } = useFormContext()
	const { fields: formPickedColumns } = useFieldArray({
		control,
		name: 'columns',
	})

	// State for warning banner and modal
	const [showDeprecatedWarning, setShowDeprecatedWarning] = useState(false)
	const [isModalOpen, setIsModalOpen] = useState(false)

	// Modal handlers
	const handleOpenModal = useCallback(() => {
		setIsModalOpen(true)
	}, [])

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false)
	}, [])

	// Use either the external pickedColumns or the ones from useFieldArray
	const columnsToUse = useMemo(() => {
		return (
			externalPickedColumns ||
			(formPickedColumns as unknown as TableColumnFlat[])
		)
	}, [externalPickedColumns, formPickedColumns])

	// Check for deprecated columns whenever columns or pickedColumns change
	const enrichmentResult = useMemo(() => {
		if (!tableColumns || !columnsToUse.length) {
			return {
				validColumns: [],
				deprecatedColumns: [],
				hasDeprecatedColumns: false,
				pickedColumnsExtended: [],
			}
		}
		return validateAndCategorizeColumns({
			tableColumns,
			pickedColumns: columnsToUse,
		})
	}, [tableColumns, columnsToUse])

	// Show warning when deprecated columns are detected
	useEffect(() => {
		if (enrichmentResult.hasDeprecatedColumns) {
			setShowDeprecatedWarning(true)
		} else {
			setShowDeprecatedWarning(false)
		}
	}, [enrichmentResult.hasDeprecatedColumns])

	// Empty column template for resetting columns
	const emptyColumn = useMemo(
		() => ({
			code: '',
			uuid: '',
			foreignColumnCode: '',
			foreignColumnUuid: '',
		}),
		[],
	)

	// Handler for removing deprecated columns
	const handleRemoveDeprecatedColumns = useCallback(() => {
		// If external handler is provided, use it
		if (onRemoveDeprecatedColumns) {
			// Pass only valid columns to the handler
			onRemoveDeprecatedColumns(
				enrichmentResult.validColumns.map((vc) => vc.payload),
			)
		} else {
			// Handle form state directly
			if (enrichmentResult.deprecatedColumns.length === columnsToUse.length) {
				// If all columns are deprecated, clear all columns
				columnsToUse.forEach((_, index) => {
					setValue(`columns.${index}`, emptyColumn)
				})
			} else {
				// Otherwise, only clear deprecated columns
				enrichmentResult.deprecatedColumns.forEach((deprecatedColumn) => {
					const index = columnsToUse.findIndex(
						(pickedColumn) => pickedColumn.uuid === deprecatedColumn.uuid,
					)

					if (index !== -1) {
						setValue(`columns.${index}`, emptyColumn)
					}
				})
			}
		}

		// Reset UI state
		setShowDeprecatedWarning(false)
		handleCloseModal()
	}, [
		enrichmentResult.validColumns,
		enrichmentResult.deprecatedColumns,
		onRemoveDeprecatedColumns,
		columnsToUse,
		setValue,
		emptyColumn,
		handleCloseModal,
	])

	// Return a well-typed object with all necessary values and handlers
	return {
		// UI state
		showDeprecatedWarning,
		setShowDeprecatedWarning,
		isModalOpen,

		// Modal handlers
		handleOpenModal,
		handleCloseModal,

		// Column data
		enrichmentResult,
		pickedColumns: columnsToUse,

		// Action handlers
		handleRemoveDeprecatedColumns,
		setValue,
	}
}
